import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  CATEGORY_IDS,
  defaultCategories,
  deleteConsentCookie,
  readConsent,
  saveConsent,
} from "@/lib/cookieConsent";

const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(() => (typeof window !== "undefined" ? readConsent() : null));
  const [bannerOpen, setBannerOpen] = useState(() => typeof window !== "undefined" && !readConsent());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [draftCategories, setDraftCategories] = useState(() => defaultCategories(false));

  useEffect(() => {
    const onChange = (e) => setConsent(e.detail || readConsent());
    window.addEventListener("sd-cookie-consent", onChange);
    return () => window.removeEventListener("sd-cookie-consent", onChange);
  }, []);

  useEffect(() => {
    if (consent?.categories) {
      setDraftCategories({ ...consent.categories });
    }
  }, [consent]);

  const hasConsent = useCallback(
    (category) => {
      if (category === "necessary") return true;
      return Boolean(consent?.categories?.[category]);
    },
    [consent]
  );

  const acceptAll = useCallback(() => {
    const cats = defaultCategories(true);
    const record = saveConsent({ state: "allow_all", categories: cats });
    setConsent(record);
    setBannerOpen(false);
    setSettingsOpen(false);
  }, []);

  const denyAll = useCallback(() => {
    const cats = defaultCategories(false);
    const record = saveConsent({ state: "deny_all", categories: cats });
    setConsent(record);
    setBannerOpen(false);
    setSettingsOpen(false);
  }, []);

  const savePreferences = useCallback((categories) => {
    const merged = { ...defaultCategories(false), ...categories, necessary: true };
    const allOn = CATEGORY_IDS.every((id) => merged[id]);
    const allOff = CATEGORY_IDS.filter((id) => id !== "necessary").every((id) => !merged[id]);
    const state = allOn ? "allow_all" : allOff ? "deny_all" : "custom";
    const record = saveConsent({ state, categories: merged });
    setConsent(record);
    setBannerOpen(false);
    setSettingsOpen(false);
  }, []);

  const withdrawConsent = useCallback(() => {
    deleteConsentCookie();
    setConsent(null);
    setDraftCategories(defaultCategories(false));
    setBannerOpen(true);
    setSettingsOpen(false);
  }, []);

  const openSettings = useCallback(() => {
    setDraftCategories(consent?.categories ? { ...consent.categories } : defaultCategories(false));
    setSettingsOpen(true);
    setBannerOpen(false);
  }, [consent]);

  const value = useMemo(
    () => ({
      consent,
      bannerOpen,
      settingsOpen,
      draftCategories,
      setDraftCategories,
      hasConsent,
      acceptAll,
      denyAll,
      savePreferences,
      withdrawConsent,
      openSettings,
      setSettingsOpen,
      setBannerOpen,
    }),
    [
      consent,
      bannerOpen,
      settingsOpen,
      draftCategories,
      hasConsent,
      acceptAll,
      denyAll,
      savePreferences,
      withdrawConsent,
      openSettings,
    ]
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error("useCookieConsent must be used within CookieConsentProvider");
  return ctx;
}
